'use client';

import { FC, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhysicianFinderDisclaimer } from '../components';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Field,
  FieldContent,
  FieldError,
  DialogContent,
  Dialog,
} from '@shared/shared-shadcn-ui';
import { cn } from '@shared/shared-utils';
import { FormButton } from '../../../../_libs/components/components';
import { HealthcareGuidanceCard } from '../healthcare-guidance-card/healthcare-guidance-card';
import {
  findNearDoctorFormValidationSchema,
  type FindNearDoctorFormData,
} from './libs/validation-schemas/find-near-doctor-form.validation-schema';
import {
  fetchDoctors,
  type GetDoctorsApiResponse,
} from 'apps/woundscantwait-com/src/packages/doctors/doctors';
import { ClosestLocations } from '../closest-doctors/closest-doctors';
import { useQuery } from 'react-query';
import { Button } from 'apps/woundscantwait-com/src/libs/components/components';
import ArrowRightIcon from '../../../../../../public/svg/arrow-right.svg';

const FindNearDoctor: FC = () => {
  const hasShownAlert = useRef<boolean>(false);
  const [showTableResults, setShowTableResults] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<FindNearDoctorFormData>({
    resolver: zodResolver(findNearDoctorFormValidationSchema),
    defaultValues: {
      zip: '',
      radius: '5',
    },
  });

  const zipFormValue = watch('zip');
  const radiusFormValue = watch('radius');

  const { data, isLoading } = useQuery<GetDoctorsApiResponse>({
    queryKey: ['doctors', zipFormValue, radiusFormValue],
    queryFn: async () => {
      const zip = getValues('zip');
      const radius = getValues('radius');

      return fetchDoctors(zip, radius);
    },
    enabled: showTableResults,
    onSuccess: (data) => {
      if (data.results.length === 0 && !hasShownAlert.current) {
        setShowAlert(true);
        hasShownAlert.current = true;
      }
    },
  });

  const onSubmit = () => {
    setShowTableResults(true);
  };
  const zipValue = watch('zip');

  if (showTableResults) {
    return (
      <>
        <ClosestLocations
          doctors={data?.results ?? []}
          zip={zipValue}
          radius={watch('radius')}
          onBackToSearch={() => setShowTableResults(false)}
          onSearchWithinChange={(value) => setValue('radius', value)}
          isLoading={isLoading}
        />
        <Dialog
          onOpenChange={() => {
            setShowAlert(false);
          }}
          open={showAlert}
        >
          <DialogContent className="bg-white lg:h-[725px] py-6 lg:px-12 lg:py-10 max-w-[90%] lg:max-w-[1000px]">
            <div className="text-ogn-blue space-y-4 font-bold text-[20px] lg:text-[26px]">
              <p>
                It looks like there aren’t any matches that meet your search
                criteria at this time, but don’t be discouraged! You can expand
                your search radius and try again, or you can reach out to your
                primary care provider.
              </p>
              <p>
                When you find a healthcare professional to care for your wound,
                you can ask them which Organogenesis treatment would be the best
                choice for your wound.
              </p>
            </div>
            <Button
              className="mx-auto my-auto w-[260px] lg:w-[300px] flex items-center justify-center gap-x-2 text-[22px] font-roboto-condensed lg:text-[24px] rounded-full"
              variant="blue"
              onClick={() => {
                setShowAlert(false);
              }}
            >
              <span>Continue</span>
              <ArrowRightIcon className="w-6 h-6" />
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="w-full bg-white border-b-[3px] pb-8 lg:pb-16 lg:pt-12 border-b-ogn-bright-yellow lg:rounded-t-[6px] lg:pl-12 pr-5">
      <h3 className="text-[24px] lg:text-[30px] mx-auto">
        Enter your ZIP code to find a doctor near you.
      </h3>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-y-4 lg:flex-row lg:flex-wrap lg:ml-0 items-center gap-x-6 mt-8 justify-center"
      >
        <Controller
          control={control}
          name="zip"
          render={({ field }) => (
            <Field className="w-auto" data-invalid={!!errors.zip}>
              <FieldContent>
                <label htmlFor="zip" className="sr-only">
                  ZIP code
                </label>
                <Input
                  {...field}
                  id="zip"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  pattern="[0-9]*"
                  maxLength={5}
                  className="w-[235px] text-[20px] rounded-none h-auto text-center border-[1px] leading-normal py-2 lg:py-2 lg:text-[22px] border-ogn-blue placeholder:text-[#3D4543] md:text-[20px]"
                />
                {errors.zip && <FieldError errors={[errors.zip]} />}
              </FieldContent>
            </Field>
          )}
        />
        <div className="flex items-center gap-x-4">
          <span id="within-label" className="text-[20px] lg:text-[24px] text-ogn-blue font-bold">
            Within:
          </span>
          <Controller
            control={control}
            name="radius"
            render={({ field }) => (
              <Field data-invalid={!!errors.radius}>
                <FieldContent>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={zipFormValue === ''}
                  >
                    <SelectTrigger aria-labelledby="within-label" aria-label="Select search radius" className="!relative !z-[30] inline-flex items-center justify-center rounded-full bg-white border-0 w-[195px] capitalize h-auto leading-normal py-3 text-[20px] lg:text-[22px] drop-shadow-[0_3px_6px_rgba(0,0,0,0.161)] px-6 [&>svg]:h-6 [&>svg]:w-6">
                      <SelectValue placeholder="Select miles" />
                    </SelectTrigger>
                    <SelectContent
                      withPortal={false}
                      className="bg-[#F2F2F2] border-0 rounded-none -top-12 !z-[25]"
                    >
                      {[5, 10, 15, 20, 25, 30, 35, 45, 50].map(
                        (item, index) => (
                          <SelectItem
                            key={item}
                            value={item.toString()}
                            className={cn(
                              'hover:bg-[#E5E5E5] focus:bg-[#E5E5E5] text-[18px] lg:text-[20px] cursor-pointer py-2',
                              'data-[state=checked]:bg-ogn-blue data-[state=checked]:text-white',
                              {
                                'mt-14': index === 0,
                              },
                            )}
                          >
                            {item} miles
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  {errors.radius && <FieldError errors={[errors.radius]} />}
                </FieldContent>
              </Field>
            )}
          />
        </div>
      </form>
      <div className="mt-8 flex items-center justify-center">
        <FormButton
          className="w-[260px] lg:w-[300px]"
          onClick={handleSubmit(onSubmit)}
          isDisabled={!isValid}
        >
          Search
          <ArrowRightIcon className="!w-5 !h-5" />
        </FormButton>
      </div>
      <PhysicianFinderDisclaimer className="mt-8 max-w-[960px]" />
      <HealthcareGuidanceCard />
    </div>
  );
};

export { FindNearDoctor };
